import { z } from "zod/v4";

import {
  FindingKindSchema,
  SectionTypeSchema,
} from "../../../functions/opportunity-audit/section-reading";

/**
 * What each audit node decides, one schema per node.
 *
 * **These are leaves and they churn.** Each is a snapshot of what that node
 * emits today, so a node's design pass updates its own schema as part of
 * implementing it — and because the node's builder is typed against the schema,
 * dropping a field it promised is a type error rather than a silent omission.
 * A leaf is part of every stored card, so an edit here changes the card's
 * stored shape and the shape record (`stored-shapes.spec.ts`) asks for it.
 *
 * A leaf requires only what the node emits today and what every stored card
 * already has. A field added later is optional, or comes with an upcaster on
 * the card. A field the node is planned to add lands when the node lands: required earlier, it fails every parse
 * written before it existed, and a value that fails to parse degrades to a
 * generic row with nothing logged, which reads as the feature being missing.
 *
 * A typed leaf says what the value is, so naming it on the card is the panel's
 * job, and the node's `rationale` carries the part no shape can reconstruct:
 * why this answer. An optional `display` is the one line the reasoning pass
 * reads for an option, where the fields alone would print as `key: value`.
 */

/** One product, named the three ways the platform needs it. */
export const RecommendationProductSchema = z.object({
  handle: z.string(),
  productId: z.string(),
  title: z.string(),
});

/**
 * `auditPricingProducts` — which products the price test covers.
 *
 * One array of products, not three parallel arrays of handles, ids and titles.
 * The node holds `RankedProduct[]` and used to take it apart on the way out,
 * which left the panel zipping three lists back together by index to draw one
 * product, and nothing in the type saying they were aligned.
 */
export const AuditPricingProductsValueSchema = z.object({
  products: z.array(RecommendationProductSchema).min(1),
});

/** One price group's move: variants sharing a price move together. */
export const AuditPriceMoveSchema = z.object({
  handle: z.string(),
  productId: z.string(),
  title: z.string(),
  /** Every variant sharing this price. The platform builds on these. */
  variantIds: z.array(z.string()),
  /** Named only when the group is a single variant; null when it is several. */
  variantTitle: z.string().nullable(),
  currentPrice: z.number(),
  /** Rounded to the store's own price ending, so it reads as a real price. */
  price: z.number(),
  /**
   * Signed, and measured on the ROUNDED price rather than the intended move,
   * so entries in one arm differ slightly from each other and from the arm's
   * own `intendedChange`. Show the arm's number, not this one.
   */
  change: z.number(),
  /**
   * The shop currency, where Shopify gave us one. It rides on the entered unit
   * cost, so it is null on a product with no cost entered.
   */
  currencyCode: z.string().nullish(),
  requiredVolumeRetention: z.number().nullable(),
  requiredVolumeLift: z.number().nullable(),
  costBasis: z.string(),
});

/** One test group other than control. */
export const AuditPriceArmSchema = z.object({
  direction: z.enum(["up", "down"]),
  /**
   * The move this arm was sized at, before any price was rounded. Every product
   * in the arm shares it, which is what makes one percentage per group an
   * honest thing to show.
   *
   * Optional because a payload written before it existed is still a price test
   * worth drawing, and `armChange` recovers the arm's move from its prices when
   * it is absent. A current node always sends it, and
   * `pricing-prices.tool.spec.ts` locks that.
   */
  intendedChange: z.number().optional(),
  prices: z.array(AuditPriceMoveSchema).min(1),
});

export const AuditPriceExclusionSchema = z.object({
  handle: z.string(),
  reason: z.enum([
    "bundle",
    "subscription",
    "noPrice",
    "notFound",
    "overLimit",
  ]),
});

/** `auditPricingPrices` — what the new price is, per product. */
export const AuditPricingPricesValueSchema = z.object({
  testType: z.enum(["priceStraddle", "priceRise", "priceCut"]),
  /** Including control, which carries no move of its own. */
  arms: z.union([z.literal(2), z.literal(3)]),
  daysToDecision: z.number().nullish(),
  /** The non-control groups, so one fewer than `arms`. */
  moves: z.array(AuditPriceArmSchema).min(1),
  excluded: z.array(AuditPriceExclusionSchema).optional(),
});

/**
 * `auditShippingZone` — which zone the shipping test changes, or the store's
 * home market when its zones could not be read.
 */
export const AuditShippingZoneValueSchema = z.union([
  z.object({
    /** With `profileId` and `locationGroupId`, what the platform builds the test from. */
    zoneId: z.string(),
    profileId: z.string(),
    locationGroupId: z.string(),
    zoneName: z.string(),
    profileName: z.string(),
    countryCodes: z.array(z.string()),
    /** A free method with no condition at all, not one free above an amount. */
    hasFreeShippingMethod: z.boolean(),
    /** The zone's name, with its profile's when another zone shares the name. */
    display: z.string().optional(),
  }),
  z.object({
    homeMarket: z.literal(true),
    countryCode: z.string(),
    countryName: z.string(),
  }),
]);

/**
 * `auditShippingChange` — what changes in that zone.
 *
 * `both` is accepted from a merchant who asks for it but never proposed: one
 * result carries one number.
 */
export const AuditShippingChangeValueSchema = z.enum([
  "threshold",
  "rate",
  "both",
  "introduceThreshold",
]);

/**
 * `auditShippingValue` — the number the change moves to.
 *
 * `amount`, not `value`: the node used to call it `value` inside a value, so a
 * reader reached it as `newValue.value.value`.
 */
export const AuditShippingValueValueSchema = z.object({
  amount: z.number(),
  /** Null where the store's own currency was not readable. */
  currency: z.string().nullable(),
  applies: z.enum(["threshold", "rate"]),
  /** Present only where the store already had a threshold to raise. */
  current: z.number().optional(),
  /** The paid rate a `both` change moves beside the threshold. */
  rate: z.object({ amount: z.number(), current: z.number() }).optional(),
  /** Share of orders that already reach it, where we could measure. */
  reach: z.number().nullable(),
  display: z.string().optional(),
  /**
   * The zone's methods a test of this amount replaces, with what they charge
   * today: the paid method it moves or charges below the threshold, and the
   * free method above the threshold where the zone has one. Null when the paid
   * method is priced by weight or in tiers, which a flat rate cannot copy.
   * Optional because cards shown before it have none.
   */
  replaces: z
    .object({
      paid: z.object({
        methodId: z.string(),
        name: z.string(),
        amount: z.number(),
      }),
      free: z
        .object({
          methodId: z.string(),
          name: z.string(),
          threshold: z.number(),
        })
        .nullable(),
    })
    .nullable()
    .optional(),
});

/** `auditOfferType` — which offer to make. */
export const AuditOfferTypeValueSchema = z.object({
  offerType: z.enum([
    "gwp",
    "bxgy",
    "volumeDiscount",
    "freeShipping",
    "dollarOff",
    "percentOff",
  ]),
  /**
   * Free shipping only: the rates it makes free, by name. Each zone's cheapest
   * paid method. Optional because other offers, and cards shown before it,
   * have none.
   */
  freeRates: z.array(z.string()).optional(),
});

/** `auditOfferProducts` — what the offer applies to. */
export const AuditOfferProductsValueSchema = z.discriminatedUnion("scope", [
  z.object({ scope: z.literal("wholeStore") }),
  z.object({
    scope: z.literal("specificProducts"),
    products: z.array(RecommendationProductSchema).min(1),
  }),
]);

/**
 * `auditOfferTerms` — the offer's own numbers, which differ by offer.
 *
 * Discriminated on `offerType` rather than flattened, because a threshold and
 * a buy quantity are not the same field with different names, and a reader of
 * one arm should not have to know which of the other five's fields are null.
 */
/**
 * `headroom` is a share from 0 to 1: how much of what the offer can afford to
 * give away is left unspent at these terms.
 */
export const AuditOfferTermsValueSchema = z.discriminatedUnion("offerType", [
  z.object({
    offerType: z.literal("volumeDiscount"),
    buyQuantity: z.number(),
    discount: z.number(),
    headroom: z.number(),
  }),
  z.object({
    offerType: z.literal("bxgy"),
    buyQuantity: z.number(),
    getQuantity: z.number(),
    headroom: z.number(),
  }),
  z.object({
    offerType: z.literal("freeShipping"),
    threshold: z.number().nullable(),
    currency: z.string().nullable(),
    /** Share of orders that already clear the threshold. */
    reach: z.number().nullish(),
  }),
  z.object({
    offerType: z.literal("dollarOff"),
    amount: z.number(),
    currency: z.string(),
    headroom: z.number(),
  }),
  z.object({
    offerType: z.literal("percentOff"),
    discount: z.number(),
    headroom: z.number(),
  }),
  z.object({
    offerType: z.literal("gwp"),
    /** Absent until we have the order history to place a threshold. */
    threshold: z.number().nullish(),
    giftCostCeiling: z.number().nullish(),
    currency: z.string().nullish(),
    reach: z.number().nullish(),
    giftProductId: z.string().nullish(),
  }),
]);

/**
 * The kinds of page a content test can go on, typed from the path's shape, and
 * `every` for a change on every page of the store.
 */
export const AuditContentPageTypeSchema = z.enum([
  "homepage",
  "product",
  "collection",
  "page",
  "blog",
  "article",
  "cart",
  "search",
  "listOfCollections",
  "every",
]);

/** `auditContentPage` — which page the content test runs on. */
export const AuditContentPageValueSchema = z.object({
  path: z.string(),
  pageType: AuditContentPageTypeSchema,
  /** A product page named by its product's title, which its handle only approximates. */
  display: z.string().optional(),
});

/** The every-page option's path. */
export const EVERY_PAGE_PATH = "*";

/** Whether a content page is every page: the header, bars and footer every page shows. */
export function isEveryPage(page: {
  path?: string;
  pageType: string;
}): boolean {
  return page.pageType === "every" || page.path === EVERY_PAGE_PATH;
}

/** A section's type, as the section reading answers it; `addToCart`, a type of past cards, reads as `buyBox`. */
export const AuditContentLocationLabelSchema = z.preprocess(
  (value) => (value === "addToCart" ? "buyBox" : value),
  SectionTypeSchema
);

/**
 * A card stored before 2026-10-19 calls the theme's name `sectionType` and its
 * type `label`; a value with no `themeName` is read that way.
 */
function fromPastLocation(value: unknown): unknown {
  if (typeof value !== "object" || value === null) {
    return value;
  }
  const { label, ...rest } = value as Record<string, unknown>;
  if (rest.type === "section" && !("themeName" in rest)) {
    const { sectionType: themeName, ...others } = rest;
    return { ...others, themeName, ...(label ? { sectionType: label } : {}) };
  }
  if (rest.type === "wholePage" && label !== undefined) {
    return { ...rest, sectionType: label };
  }
  return value;
}

/**
 * `auditContentLocation` — the section on that page a test changes.
 *
 * The section itself (ruled 2026-09-14): what gets built is a selector on it.
 * `wholePage` is a page whose sections could not be read (ruled 2026-09-15).
 */
export const AuditContentLocationValueSchema = z.preprocess(
  fromPastLocation,
  z.discriminatedUnion("type", [
    z.object({
      type: z.literal("section"),
      /** The section as the theme names it, e.g. `image-with-text`. */
      themeName: z.string(),
      /** What kind of section it is, from the section reading; absent when the page has no reading. */
      sectionType: AuditContentLocationLabelSchema.optional(),
      /** The section's key in its template or group. */
      sectionKey: z.string(),
      /** Where to build: the section's own wrapper on the rendered page. */
      selector: z.string(),
      /** Position among the page's own sections, starting at 1. */
      position: z.number(),
      /** Whether the change shows on this page or on every page. */
      renderScope: z.enum(["page", "sitewide"]),
      /**
       * What the section says as served, one line per block. Null when its
       * markup was not read. Optional because cards shown before it have none.
       */
      text: z.string().nullable().optional(),
      /** Whether a script or an app may fill it after the page loads, so its served text can be incomplete. */
      fillsLater: z.boolean().nullable().optional(),
      /** What it shows when the page loads, by its section reading. */
      shows: z.array(FindingKindSchema).optional(),
      /** The merchant's words that mean this section, as the location matcher read them. */
      mentions: z.array(z.string()).optional(),
    }),
    z.object({
      type: z.literal("wholePage"),
      sectionType: AuditContentLocationLabelSchema.optional(),
      /**
       * What a page builder's page says, one line per block. Null when it was
       * not read. Optional because cards shown before it have none.
       */
      text: z.string().nullable().optional(),
    }),
  ])
);

/** `auditContentChange` — what kind of change to make there. */
export const AuditContentChangeValueSchema = z.enum([
  // The experiment classifier's change types, so a recommendation and a past
  // test use the same word. Without its two labelling-only values.
  "product-media",
  "purchase-action-ux",
  "default-selections",
  "product-education",
  "value-proposition-messaging",
  "offer-messaging",
  "product-sort",
  "content-hierarchy",
  "page-layout",
  "upsell",
  "cross-sell",
  "social-proof",
  "urgency-and-scarcity",
  "trust-signals",
  "pricing-display",
  "microcopy",
]);

/** Each content change in a merchant's words, for every surface that names one. */
export const CONTENT_CHANGE_WORDS: Record<
  z.infer<typeof AuditContentChangeValueSchema>,
  string
> = {
  "product-media": "The product images and video",
  "purchase-action-ux": "How adding to cart works",
  "default-selections": "What is selected by default",
  "product-education": "Explaining the product",
  "value-proposition-messaging": "The reasons to buy",
  "offer-messaging": "How the offer is shown",
  "product-sort": "The order products are listed in",
  "content-hierarchy": "The order things appear in",
  "page-layout": "The page layout",
  upsell: "An upsell",
  "cross-sell": "A cross-sell",
  "social-proof": "Social proof",
  "urgency-and-scarcity": "Urgency and scarcity",
  "trust-signals": "Trust signals",
  "pricing-display": "How the price is shown",
  microcopy: "The wording",
};

/**
 * `auditContentAlternative` — how the change is tested, in one shape whether
 * the audit offered it or the merchant named it, so the two can be matched.
 */
export const AuditContentAlternativeValueSchema = z.object({
  type: z.literal("mechanism"),
  mechanism: z.enum(["visualEditor", "url", "template", "theme"]),
  /** What the split is assigned over. */
  buildScope: z.enum(["page", "template", "store"]),
  /** How much work building the variant is. */
  buildCost: z.enum(["setting", "development"]),
  /** The URL, template or theme the merchant named, when they named one. */
  alternative: z.string().optional(),
  /** A named theme's numeric id, when it resolved. */
  themeId: z.string().optional(),
  candidateUpdatedAt: z.string().optional(),
  /** Both legs of a theme test carry the live theme, so it cannot be built without it. */
  liveTheme: z.string().optional(),
  liveThemeId: z.string().optional(),
});

/** `auditCheckoutBlock` — which checkout block the test changes. */
export const AuditCheckoutBlockValueSchema = z.object({
  blockType: z.string(),
  overrides: z.record(z.string(), z.unknown()),
});

/** `auditCheckoutContent` — what that block says. */
export const AuditCheckoutContentValueSchema = z.object({
  blockType: z.string(),
  overrides: z.object({
    variables: z.record(z.string(), z.unknown()),
    styles: z.record(z.string(), z.unknown()).optional(),
  }),
  upsellProductRules: z.unknown().optional(),
});

/** `auditTestMode` — how it runs. */
export const AuditTestModeValueSchema = z.enum([
  "experiment",
  "personalization",
]);

/** `auditKeyMetric` — what it is judged on. */
export const AuditKeyMetricValueSchema = z.enum([
  "profitPerVisitor",
  "netRevenuePerVisitor",
  /** Post-purchase: revenue from the lines an accepted offer added. */
  "netRevenueFromPostPurchaseOffer",
]);

export type RecommendationProduct = z.infer<typeof RecommendationProductSchema>;
export type AuditPricingProductsValue = z.infer<
  typeof AuditPricingProductsValueSchema
>;
export type AuditPricingPricesValue = z.infer<
  typeof AuditPricingPricesValueSchema
>;
export type AuditOfferTermsValue = z.infer<typeof AuditOfferTermsValueSchema>;
export type AuditContentLocationLabelType = z.infer<
  typeof AuditContentLocationLabelSchema
>;
export type AuditContentLocationValueType = z.infer<
  typeof AuditContentLocationValueSchema
>;
export type AuditPriceArmType = z.infer<typeof AuditPriceArmSchema>;
export type AuditPriceMoveType = z.infer<typeof AuditPriceMoveSchema>;
export type AuditPriceExclusionType = z.infer<typeof AuditPriceExclusionSchema>;
